"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderComponentDiagram = renderComponentDiagram;
function renderComponentDiagram(descriptors, title, options = { id: 'component-backend', source: 'buildBackend → describePorts' }) {
    const components = [...new Set(descriptors.map((d) => d.component))].sort();
    const provided = descriptors.filter((d) => d.kind === 'provided');
    // ENVIRONMENT PORTS ARE NOT SEAMS BETWEEN COMPONENTS (2026-09-11). A socket whose contract is
    // `local` names a platform service — a clock, an id source, entropy — that the hosting process
    // fills from its own host, and that every process has its own copy of. Pairing it against a
    // lollipop would draw a connector to a component that does not exist; listing it as "wired to a
    // supplier outside this diagram" would say less than is known. It gets its own section.
    const environment = descriptors
        .filter((d) => d.kind === 'required' && d.contract?.local === true)
        .sort((a, b) => a.name.localeCompare(b.name));
    const required = descriptors.filter((d) => d.kind === 'required' && d.contract?.local !== true);
    const providedNames = new Set(provided.map((d) => d.name));
    const requiredNames = new Set(required.map((d) => d.name));
    // Carry the DESCRIPTORS through, not just their names. An earlier version matched on names and
    // then looked the descriptors back up, which needed `?? '?'` fallbacks on every lookup — dead
    // branches that could not be reached and could not be covered, which §28.18 calls a broken
    // signature rather than a defensive guard. Pairing once, here, removes them structurally.
    const byName = new Map(provided.map((d) => [d.name, d]));
    const pairs = required
        .flatMap((socket) => {
        const lollipop = byName.get(socket.name);
        return lollipop === undefined ? [] : [{ socket, lollipop }];
    })
        .sort((a, b) => a.socket.name.localeCompare(b.socket.name));
    // A required port with no matching lollipop is NOT automatically dangling. On the frontend the
    // providers are concrete adapters supplied by the composition root, not ports on a second
    // component, so a socket can be legitimately connected with nothing to pair it against. Split by
    // what the manifest actually says:
    //
    //   connected   → wired to a supplier this diagram does not model; draw it, do not alarm
    //   defaulted   → running on its Null fallback; that is a fact worth showing, not an error
    //   neither     → genuinely unwired, and the note is the point
    const unpaired = required.filter((d) => !providedNames.has(d.name));
    const externallyWired = unpaired
        .filter((d) => d.connected)
        .sort((a, b) => a.name.localeCompare(b.name));
    const defaultedSockets = unpaired
        .filter((d) => !d.connected && d.defaulted)
        .sort((a, b) => a.name.localeCompare(b.name));
    const danglingSockets = unpaired
        .filter((d) => !d.connected && !d.defaulted)
        .sort((a, b) => a.name.localeCompare(b.name));
    const external = options.externalCounterparties ?? {};
    const unpairedLollipops = provided
        .filter((d) => !requiredNames.has(d.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    const externalLollipops = unpairedLollipops.filter((d) => Object.prototype.hasOwnProperty.call(external, d.name));
    const unusedLollipops = unpairedLollipops.filter((d) => !Object.prototype.hasOwnProperty.call(external, d.name));
    const out = [];
    out.push(`@startuml ${options.id}`);
    out.push("'");
    out.push("' GENERATED FILE — do not edit by hand.");
    out.push(`' Produced from the real assembled graph (${options.source}).`);
    out.push("' Regenerate with: npm run uml:generate");
    out.push("' A test fails if this file and the generator disagree.");
    out.push("'");
    out.push('');
    out.push('skinparam componentStyle rectangle');
    out.push('left to right direction');
    out.push('skinparam backgroundColor #1a1a2e');
    out.push('skinparam component {');
    out.push('  BackgroundColor #16213e');
    out.push('  BorderColor #0f3460');
    out.push('  FontColor #e0e0e0');
    out.push('}');
    out.push('skinparam interface {');
    out.push('  BackgroundColor #0f3460');
    out.push('  BorderColor #e94560');
    out.push('  FontColor #e0e0e0');
    out.push('}');
    out.push('skinparam arrow {');
    out.push('  Color #e94560');
    out.push('  FontColor #a0a0c0');
    out.push('}');
    out.push('');
    out.push(`title ${title}`);
    out.push('');
    for (const component of components) {
        out.push(`component "${component}" as ${alias(component)}`);
    }
    out.push('');
    // One interface per matched name: the provider draws the lollipop, the consumer the socket.
    //
    // ⚠ "PER NAME" WAS A CLAIM THE LOOP DID NOT HONOUR UNTIL 2026-09-09. It emitted one `interface`
    // declaration and one provider line PER PAIR, which is the same thing only while every port name
    // has exactly one consumer — true of this graph until `DomainIdGenerator` acquired a second one.
    // Two consumers then produced a duplicate alias declaration and a duplicate provider edge.
    // PlantUML tolerates both, so nothing would have failed; the diagram would just have quietly
    // started repeating itself, which is the failure mode a generated diagram exists to avoid.
    // `pushOnce` makes the comment true: declarations and provider edges are deduplicated, consumer
    // arrows never are — a shared port with three consumers must draw three arrows.
    const emitted = new Set();
    const pushOnce = (line) => {
        if (emitted.has(line))
            return;
        emitted.add(line);
        out.push(line);
    };
    out.push(`' ${pairs.length} assembled connector(s)`);
    for (const { socket, lollipop } of pairs) {
        const iface = interfaceAlias(socket.name);
        // A defaulting port running on its fallback is a load-bearing distinction, so the diagram
        // says so rather than drawing it as a plain dependency.
        // A stream port is a subscription carried by a broker rather than by request/reply — the one
        // connector the kernel transport refuses, so the picture says so.
        const note = socket.defaulted ? ' : <<defaulted>>' : socket.contract?.stream === true ? ' : <<stream>>' : '';
        pushOnce(`interface "${socket.name}" as ${iface}`);
        pushOnce(`${alias(lollipop.component)} -- ${iface}`);
        out.push(`${alias(socket.component)} ..> ${iface}${note}`);
    }
    out.push('');
    if (environment.length > 0) {
        out.push(`' ${environment.length} environment port(s) — platform services filled by the hosting process, not by a component`);
        for (const d of environment) {
            const iface = interfaceAlias(d.name);
            pushOnce(`interface "${d.name}" as ${iface} <<environment>>`);
            out.push(`${alias(d.component)} ..> ${iface} : <<local>>`);
        }
        out.push('');
    }
    if (externallyWired.length > 0) {
        out.push(`' ${externallyWired.length} socket(s) wired to a supplier outside this diagram`);
        for (const d of externallyWired) {
            const iface = interfaceAlias(d.name);
            pushOnce(`interface "${d.name}" as ${iface}`);
            out.push(`${alias(d.component)} ..> ${iface}`);
        }
        out.push('');
    }
    if (defaultedSockets.length > 0) {
        out.push(`' ${defaultedSockets.length} socket(s) running on their Null fallback`);
        for (const d of defaultedSockets) {
            const iface = interfaceAlias(d.name);
            pushOnce(`interface "${d.name}" as ${iface}`);
            out.push(`${alias(d.component)} ..> ${iface} : <<defaulted>>`);
        }
        out.push('');
    }
    if (danglingSockets.length > 0) {
        out.push(`' ${danglingSockets.length} REQUIRED port(s) with no provider — the component cannot be fully assembled`);
        for (const d of danglingSockets) {
            out.push(`note as N_${sanitise(d.name)}`);
            out.push(`  UNCONNECTED required port "${d.name}"`);
            out.push(`  on ${d.component}`);
            out.push('end note');
        }
        out.push('');
    }
    if (unusedLollipops.length > 0) {
        out.push(`' ${unusedLollipops.length} PROVIDED port(s) nothing requires`);
        for (const d of unusedLollipops) {
            out.push(`note as U_${sanitise(d.name)}`);
            out.push(`  Provided port "${d.name}" has no consumer`);
            out.push(`  on ${d.component}`);
            out.push('end note');
        }
        out.push('');
    }
    if (externalLollipops.length > 0) {
        out.push(`' ${externalLollipops.length} PROVIDED port(s) consumed by an EXTERNAL counterparty`);
        for (const d of externalLollipops) {
            out.push(`note as X_${sanitise(d.name)}`);
            out.push(`  Provided port "${d.name}" on ${d.component}`);
            out.push(`  is consumed by: ${external[d.name]}`);
            out.push('end note');
        }
        out.push('');
    }
    out.push('@enduml');
    return out.join('\n') + '\n';
}
/** PlantUML aliases cannot contain punctuation; component names are model names and might. */
function alias(component) {
    return sanitise(component);
}
/** Prefixed, because PlantUML type identifiers are global — see the header. */
function interfaceAlias(port) {
    return `I_${sanitise(port)}`;
}
function sanitise(value) {
    return value.replace(/[^A-Za-z0-9_]/g, '_');
}
//# sourceMappingURL=renderComponentDiagram.js.map